import { useEffect, useState } from 'react';
import { Bot, Loader2, Play, Plus, Square, Store, Archive, Trash2 } from 'lucide-react';
import {
  createPlaygroundAgent,
  deleteAgent,
  fetchAgentStatus,
  syncAgent,
  startAgent,
  stopAgent,
  unsyncAgent,
} from '../../api/playground';
import { getPersonaTemplates, type PersonaTemplate } from '../../api/persona';
import { useAgentRuntimeStatus, isRuntimeRunning } from '../../hooks/useAgentRuntimeStatus';
import { toast } from '../../utils/toast';
import Modal from '../ui/Modal';
import type { AgentStatusInfo, PlaygroundAgent } from '../../types';

/**
 * 工作台左栏 —— Agent 列表。
 *
 * Props 契约（后续工作台任务扩展时保持兼容）：
 * - agents:        bootstrap 返回的 Agent 列表（由 Workbench 统一拉取后传入）
 * - loading:       首屏加载中
 * - error:         bootstrap 拉取失败的错误信息
 * - selectedAgentId: 当前选中 Agent（可为 null）
 * - onSelectAgent:  点击某行 Agent 时回调，参数为该 Agent 的 id
 * - onAgentCreated: 可选；新建 Agent 成功后回传新 Agent，供 Workbench 更新列表并选中
 * - onRefresh:      可选；发布成功后刷新 bootstrap
 *
 * 内部职责：
 * - 运行状态点：全量一次性拉取 + 选中 Agent 每 5s 轮询（页面不可见时暂停）；
 * - 行内启停按钮（start 中 spinner；422 提示去配模型）+ 平台体验模型小徽标；
 * - 「启动服务」「同步到应龙」：确认对话框 → 调用端点 → toast + 刷新 bootstrap；
 * - 「新建 Agent」对话框：harness 框架 + 人设模板卡片精选区（4-6 个常用模板）。
 */
export interface AgentListPanelProps {
  agents: PlaygroundAgent[];
  loading: boolean;
  error: string | null;
  selectedAgentId: string | null;
  onSelectAgent: (agentId: string) => void;
  onAgentCreated?: (agent: PlaygroundAgent) => void;
  /** 删除 Agent 成功后回调（仅当删的是当前选中项），供 Workbench 清空选中 */
  onAgentDeleted?: (agentId: string) => void;
  /** 发布成功后刷新 bootstrap */
  onRefresh?: () => void;
}

/** harness 框架选项与中文说明（新建对话框下拉） */
const FRAMEWORK_OPTIONS = [
  { value: 'openclaw', label: 'OpenClaw', desc: '轻量推荐：上手快，适合日常对话练习' },
  { value: 'hermes', label: 'Hermes', desc: '通用对话 Agent，灵活可扩展' },
  { value: 'opencode', label: 'OpenCode', desc: '偏编程助手场景，适合代码练习' },
  { value: 'generic', label: 'Generic', desc: '通用运行时，最小依赖' },
] as const;

const FRAMEWORK_LABELS: Record<string, string> = {
  openclaw: 'OpenClaw',
  hermes: 'Hermes',
  opencode: 'OpenCode',
  generic: 'Generic',
};

/**
 * 人设模板精选区：前端内置推荐顺序 / emoji / 一句话简介。
 * 从 /persona/templates 按 name 匹配，未匹配到的模板不展示（最多展示 6 个）。
 */
const CURATED_TEMPLATES: Array<{ name: string; emoji: string; intro: string }> = [
  { name: '通用智能助手', emoji: '🤖', intro: '什么都聊得来，最适合第一次体验' },
  { name: '专业客服代表', emoji: '🎧', intro: '懂礼貌、会安抚，练客服场景' },
  { name: '技术顾问', emoji: '🛠️', intro: '把技术问题讲明白，适合编程咨询' },
  { name: '知识导师', emoji: '📚', intro: '把知识点拆小讲透，适合教学练习' },
  { name: '创意写作助手', emoji: '✍️', intro: '点子多、文笔好，写文案不发愁' },
  { name: '产品经理', emoji: '📋', intro: '会提问、会梳理需求，练产品思维' },
];

/** 按精选顺序过滤 /persona/templates 返回的模板列表 */
function pickCuratedTemplates(list: PersonaTemplate[]): PersonaTemplate[] {
  const byName = new Map(list.map((t) => [t.name, t]));
  return CURATED_TEMPLATES.map((c) => byName.get(c.name))
    .filter((t): t is PersonaTemplate => !!t)
    .slice(0, 6);
}

/** 精选模板的展示元数据（emoji / 一句话简介） */
function curatedMeta(name: string | undefined): { emoji: string; intro: string } {
  const found = CURATED_TEMPLATES.find((c) => c.name === name);
  if (found) return { emoji: found.emoji, intro: found.intro };
  return { emoji: '💡', intro: '套用它作为 Agent 的性格起点' };
}

function statusMeta(status: string | undefined): { cls: string; label: string } {
  switch (status) {
    case 'running':
      return { cls: 'status-dot--running', label: '运行中' };
    case 'stopped':
      return { cls: 'status-dot--stopped', label: '已停止' };
    case 'none':
      return { cls: 'status-dot--none', label: '未启动' };
    case 'error':
      return { cls: 'status-dot--error', label: '异常' };
    default:
      return status ? { cls: 'status-dot--busy', label: status } : { cls: '', label: '未知' };
  }
}

function describeError(err: unknown): string {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: unknown };
  if (e.response?.data && typeof e.response.data.detail === 'string') return e.response.data.detail;
  return typeof e.message === 'string' ? e.message : '操作失败，请稍后重试';
}

export default function AgentListPanel({
  agents,
  loading,
  error,
  selectedAgentId,
  onSelectAgent,
  onAgentCreated,
  onAgentDeleted,
  onRefresh,
}: AgentListPanelProps) {
  // 运行时状态表：选中 Agent 轮询（5s / 页面不可见暂停），其余一次性拉取
  const [statusMap, setStatusMap] = useState<Record<string, AgentStatusInfo>>({});
  const selectedStatus = useAgentRuntimeStatus(selectedAgentId, { pollMs: 5000 });

  // 选中 Agent 的状态由 hook 轮询提供；其余 Agent 挂载时一次性拉取。
  // （不做 setState 合并，渲染期直接派生，避免 effect 级联）
  useEffect(() => {
    let cancelled = false;
    const ids = agents.map((a) => a.id).filter((id) => id !== selectedAgentId);
    if (ids.length === 0) return;
    Promise.all(ids.map((id) => fetchAgentStatus(id).catch(() => null))).then((results) => {
      if (cancelled) return;
      setStatusMap((prev) => {
        const next = { ...prev };
        results.forEach((r, idx) => {
          if (r) next[ids[idx]] = r;
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [agents, selectedAgentId]);

  // 行内启停
  const [busy, setBusy] = useState<Record<string, 'starting' | 'stopping'>>({});

  const patchStatus = (agentId: string, patch: Partial<AgentStatusInfo>) => {
    setStatusMap((prev) => ({ ...prev, [agentId]: { ...(prev[agentId] ?? {}), ...patch } as AgentStatusInfo }));
  };

  const handleStart = async (agent: PlaygroundAgent) => {
    setBusy((prev) => ({ ...prev, [agent.id]: 'starting' }));
    try {
      const result = await startAgent(agent.id);
      patchStatus(agent.id, { status: result.status, endpoint_url: result.endpoint_url, error_message: result.error_message });
      if (result.status === 'running') toast(`「${agent.name}」已启动，可以开始对话啦`, 'success');
      else if (result.status === 'error') {
        // 启动失败必须给出原因（如运行时镜像缺失），不能只有一句「失败」
        const reason = result.error_message ? `：${result.error_message.slice(0, 160)}` : '';
        toast(`启动「${agent.name}」失败${reason}`, 'error');
      } else toast(`「${agent.name}」正在启动（${result.status}），稍等片刻…`, 'info');
    } catch (err) {
      const detail = describeError(err);
      if ((err as { response?: { status?: number } }).response?.status === 422) {
        toast('还差一步：请先到右侧「模型」页为该 Agent 绑定 LLM 配置，再启动。', 'error');
      } else {
        toast(detail || `启动「${agent.name}」失败`, 'error');
      }
    } finally {
      setBusy((prev) => {
        const next = { ...prev };
        delete next[agent.id];
        return next;
      });
    }
  };

  const handleStop = async (agent: PlaygroundAgent) => {
    setBusy((prev) => ({ ...prev, [agent.id]: 'stopping' }));
    try {
      const result = await stopAgent(agent.id);
      patchStatus(agent.id, { status: result.status, endpoint_url: result.endpoint_url });
      toast(`「${agent.name}」已停止`, 'success');
    } catch (err) {
      toast(describeError(err) || `停止「${agent.name}」失败`, 'error');
    } finally {
      setBusy((prev) => {
        const next = { ...prev };
        delete next[agent.id];
        return next;
      });
    }
  };

  // 新建 Agent 对话框
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createFramework, setCreateFramework] = useState<string>(FRAMEWORK_OPTIONS[0].value);
  const [createTemplateId, setCreateTemplateId] = useState<string>('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [templates, setTemplates] = useState<PersonaTemplate[]>([]);

  const openCreate = () => {
    setCreateOpen(true);
    setCreateError(null);
    setCreateName('');
    setCreateDesc('');
    setCreateFramework(FRAMEWORK_OPTIONS[0].value);
    setCreateTemplateId('');
    getPersonaTemplates()
      .then((list) => setTemplates(pickCuratedTemplates(list)))
      .catch(() => setTemplates([]));
  };

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name) {
      setCreateError('请先给 Agent 起个名字');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const agent = await createPlaygroundAgent({
        name,
        description: createDesc.trim() || undefined,
        framework: createFramework,
        template_id: createTemplateId || undefined,
      });
      onAgentCreated?.(agent);
      toast(`「${agent.name}」创建成功，去右侧配置它的「人设」吧`, 'success');
      setCreateOpen(false);
    } catch (err) {
      setCreateError(describeError(err) || '创建失败，请稍后重试');
    } finally {
      setCreating(false);
    }
  };

  // 同步到应龙（纯本地标记：该 Agent 出现在应龙「我的 Agent」，上架市场由应龙坊市管理）
  const [syncing, setSyncing] = useState<PlaygroundAgent | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);

  const handleSync = async () => {
    if (!syncing) return;
    setSyncBusy(true);
    try {
      const result = await syncAgent(syncing.id);
      toast(
        result.already
          ? `「${syncing.name}」已同步到应龙坊市，无需重复同步`
          : `「${syncing.name}」已同步到应龙坊市`,
        'success'
      );
      onRefresh?.();
      setSyncing(null);
    } catch (err) {
      toast(describeError(err) || `同步「${syncing.name}」失败`, 'error');
    } finally {
      setSyncBusy(false);
    }
  };

  // 取消同步（从应龙「我的 Agent」消失；已上架应用不受影响，需在应龙坊市下架）
  const [unsyncing, setUnsyncing] = useState<PlaygroundAgent | null>(null);
  const [unsyncBusy, setUnsyncBusy] = useState(false);

  const handleUnsync = async () => {
    if (!unsyncing) return;
    setUnsyncBusy(true);
    try {
      const result = await unsyncAgent(unsyncing.id);
      toast(
        result.already
          ? `「${unsyncing.name}」未同步到应龙坊市，无需取消`
          : `「${unsyncing.name}」已取消同步`,
        'success'
      );
      onRefresh?.();
      setUnsyncing(null);
    } catch (err) {
      toast(describeError(err) || `取消同步「${unsyncing.name}」失败`, 'error');
    } finally {
      setUnsyncBusy(false);
    }
  };

  // 删除 Agent（不可恢复：后端会连同它的运行时、会话、消息与知识条目一起清理）
  const [deleting, setDeleting] = useState<PlaygroundAgent | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await deleteAgent(deleting.id);
      toast(`「${deleting.name}」已删除`, 'success');
      // 删掉的正是当前选中项时，通知 Workbench 清空选中（否则中栏/右栏会停在一个已不存在的 Agent）
      if (deleting.id === selectedAgentId) onAgentDeleted?.(deleting.id);
      setDeleting(null);
      onRefresh?.();
    } catch (err) {
      toast(describeError(err) || `删除「${deleting.name}」失败`, 'error');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <>
      <div className="pg-panel-head">
        <span className="pg-panel-title">我的 Agent</span>
        <button className="btn btn-icon" title="新建 Agent" onClick={openCreate}>
          <Plus size={16} />
        </button>
      </div>

      {loading ? (
        <div className="pg-state">
          <Loader2 size={22} className="spin" />
          加载中...
        </div>
      ) : error ? (
        <div className="pg-state pg-error">{error}</div>
      ) : agents.length === 0 ? (
        <div className="agent-empty-guide">
          <div className="agent-empty-guide__icon">
            <Bot size={26} />
          </div>
          <div className="agent-empty-guide__title">还没有 Agent</div>
          <div className="agent-empty-guide__subtitle">三步上手练玉坊：</div>
          <ol className="agent-empty-guide__steps">
            <li>
              <span className="agent-empty-guide__step-no">1</span>
              <span>
                <b>创建</b>你的第一个 Agent
              </span>
            </li>
            <li>
              <span className="agent-empty-guide__step-no">2</span>
              <span>
                在右侧<b>配置</b>它的角色与模型
              </span>
            </li>
            <li>
              <span className="agent-empty-guide__step-no">3</span>
              <span>
                回到中栏<b>对话</b>，练手走起来
              </span>
            </li>
          </ol>
          <button type="button" className="btn btn-primary btn-block" aria-label="创建第一个 Agent" onClick={openCreate}>
            <Plus size={14} />
            新建 Agent
          </button>
        </div>
      ) : (
        <div className="agent-list">
          {agents.map((agent) => {
            const selected = agent.id === selectedAgentId;
            // 选中 Agent 用 hook 的实时轮询结果；其余用一次性拉取的 statusMap
            const runtime = selected ? (selectedStatus.info ?? statusMap[agent.id]) : statusMap[agent.id];
            const runtimeStatus = runtime?.status;
            const { cls, label } = statusMeta(runtimeStatus);
            const running = isRuntimeRunning(runtimeStatus);
            const isBusy = busy[agent.id];
            return (
              <div key={agent.id} className={`agent-card${selected ? ' agent-card--selected' : ''}`}>
                <button
                  type="button"
                  className="agent-row"
                  onClick={() => onSelectAgent(agent.id)}
                  aria-pressed={selected}
                >
                  <div className="agent-row__top">
                    <span
                      className={`status-dot ${cls}`}
                      data-testid={`status-dot-${agent.id}`}
                      title={label}
                    />
                    <span className="agent-row__name">{agent.name}</span>
                    {runtime?.model_source === 'platform' && (
                      <span className="badge badge--accent agent-row__platform" title="使用平台免费体验模型">
                        体验
                      </span>
                    )}
                    {agent.synced && (
                      <span className="badge badge--success" title="已同步到应龙坊市，可在应龙「我的 Agent」中看到">
                        已同步
                      </span>
                    )}
                    <span className={`badge${selected ? ' badge--accent' : ''}`}>
                      {agent.framework ? FRAMEWORK_LABELS[agent.framework] ?? agent.framework : '未指定'}
                    </span>
                  </div>
                  <div className="agent-row__desc">
                    {agent.description || (runtimeStatus ? `${label} · ${agent.framework || '未指定框架'}` : '未设置描述')}
                  </div>
                </button>
                <div className="agent-card__ops">
                  <button
                    type="button"
                    className="agent-op"
                    aria-label={`${running ? '停止' : '启动服务'}「${agent.name}」`}
                    title={running ? '停止运行时' : '启动临时练习运行时，进入对话'}
                    disabled={!!isBusy}
                    onClick={() => void (running ? handleStop(agent) : handleStart(agent))}
                  >
                    {isBusy ? <Loader2 size={12} className="spin" /> : running ? <Square size={12} /> : <Play size={12} />}
                    {running ? '停止' : '启动服务'}
                  </button>
                  <button
                    type="button"
                    className="agent-op"
                    aria-label={`同步到应龙「${agent.name}」`}
                    title="同步到应龙坊市，在应龙「我的 Agent」中可见；上架市场请在应龙坊市操作"
                    onClick={() => setSyncing(agent)}
                  >
                    <Store size={12} />
                    同步到应龙
                  </button>
                  {agent.synced && (
                    <button
                      type="button"
                      className="agent-op"
                      aria-label={`取消同步「${agent.name}」`}
                      title="从应龙「我的 Agent」中移除（已上架应用需在应龙坊市下架）"
                      onClick={() => setUnsyncing(agent)}
                    >
                      <Archive size={12} />
                      取消同步
                    </button>
                  )}
                  <button
                    type="button"
                    className="agent-op agent-op--danger"
                    aria-label={`删除「${agent.name}」`}
                    title="删除这个 Agent（不可恢复）"
                    onClick={() => setDeleting(agent)}
                  >
                    <Trash2 size={12} />
                    删除
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 新建 Agent 对话框 */}
      <Modal
        open={createOpen}
        title="新建 Agent"
        width="md"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setCreateOpen(false)} disabled={creating}>
              取消
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void handleCreate()} disabled={creating}>
              {creating ? <Loader2 size={14} className="spin" /> : null}
              {creating ? '创建中…' : '创建'}
            </button>
          </>
        }
      >
        <div className="form-stack">
          {createError && <div className="auth-error">{createError}</div>}
          <div className="cfg-field">
            <label className="form-label" htmlFor="new-agent-name">
              名称 <span style={{ color: 'var(--pg-danger)' }}>*</span>
            </label>
            <input
              id="new-agent-name"
              className="input"
              placeholder="比如：我的第一个人设客服"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="new-agent-desc">
              描述
            </label>
            <input
              id="new-agent-desc"
              className="input"
              placeholder="它擅长做什么？（可选）"
              value={createDesc}
              onChange={(e) => setCreateDesc(e.target.value)}
            />
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="new-agent-framework">
              Harness 框架
            </label>
            <select
              id="new-agent-framework"
              className="input"
              value={createFramework}
              onChange={(e) => setCreateFramework(e.target.value)}
            >
              {FRAMEWORK_OPTIONS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label} — {f.desc}
                </option>
              ))}
            </select>
            <div className="cfg-hint">框架决定运行时形态；OpenClaw 最轻量，推荐初学者使用。</div>
          </div>
          <div className="cfg-field">
            <label className="form-label">人设模板（可选）</label>
            {templates.length === 0 ? (
              <div className="cfg-hint">
                暂无推荐模板 —— 不套用也可以，稍后在右侧「角色」页自己写。
              </div>
            ) : (
              <>
                <div className="template-cards" role="radiogroup" aria-label="人设模板">
                  {templates.map((t) => {
                    const meta = curatedMeta(t.name);
                    const active = createTemplateId === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`template-card${active ? ' template-card--active' : ''}`}
                        onClick={() => setCreateTemplateId(active ? '' : t.id)}
                      >
                        <span className="template-card__emoji">{meta.emoji}</span>
                        <span className="template-card__body">
                          <span className="template-card__name">{t.name}</span>
                          <span className="template-card__intro">{meta.intro}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="cfg-hint">点选套用；再点一次取消。未推荐的模板稍后在「角色」页套用。</div>
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* 同步到应龙确认 */}
      <Modal
        open={!!syncing}
        title="同步到应龙"
        width="sm"
        onClose={() => setSyncing(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setSyncing(null)} disabled={syncBusy}>
              取消
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void handleSync()} disabled={syncBusy}>
              {syncBusy && <Loader2 size={14} className="spin" />}
              {syncBusy ? '同步中…' : '同步'}
            </button>
          </>
        }
      >
        <div className="confirm-text">
          确定将「{syncing?.name}」同步到应龙坊市吗？
          <br />
          同步后它会出现在应龙的「我的 Agent」中；是否上架到市场，请在应龙坊市里操作。
        </div>
      </Modal>

      {/* 取消同步确认（不影响已在应龙上架的应用） */}
      <Modal
        open={!!unsyncing}
        title="取消同步"
        width="sm"
        onClose={() => setUnsyncing(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setUnsyncing(null)} disabled={unsyncBusy}>
              取消
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void handleUnsync()} disabled={unsyncBusy}>
              {unsyncBusy && <Loader2 size={14} className="spin" />}
              {unsyncBusy ? '处理中…' : '确认取消同步'}
            </button>
          </>
        }
      >
        <div className="confirm-text">
          确定取消「{unsyncing?.name}」的同步吗？
          <br />
          取消后它将从应龙的「我的 Agent」中消失；如果已在应龙上架到市场，请前往应龙坊市下架。
        </div>
      </Modal>

      {/* 删除确认（不可恢复：连同运行时、对话记录与知识库一起清理，释放练习名额） */}
      <Modal
        open={!!deleting}
        title="删除 Agent"
        width="sm"
        onClose={() => setDeleting(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setDeleting(null)} disabled={deleteBusy}>
              取消
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => void handleDelete()}
              disabled={deleteBusy}
            >
              {deleteBusy && <Loader2 size={14} className="spin" />}
              {deleteBusy ? '删除中…' : '确认删除'}
            </button>
          </>
        }
      >
        <div className="confirm-text">
          确定删除「{deleting?.name}」吗？
          <br />
          删除后不可恢复：它的对话记录、知识库与运行时会一并清理，并释放一个练习名额。
          如果它已同步到应龙「我的 Agent」，删除后也会随之消失。
        </div>
      </Modal>
    </>
  );
}
